import { Capacitor } from "@capacitor/core";
import { type UseMutationResult, type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../contexts/FeatureFlagsProvider";
import { backendService } from "../services/backend.service";
import { useFeatureFlag } from "./useOpenFeatureFlag";

const RATING_PROMPT_STATE_QUERY_KEY = ["rating-prompt", "state"] as const satisfies readonly string[];
export const RATING_PROMPT_ANALYTICS_EVENT = "rating_prompt_native";

const APPROVED_SHIFTS_TRIGGER_COUNT = 3;
const MIN_ACCOUNT_AGE_DAYS = 3;
const MAX_APPROVAL_AGE_DAYS = 14;

type RatingPromptState = { lastReviewPromptAt: DateTime | undefined; approvedShiftRequestsCount: number; mostRecentApprovalAt: DateTime | undefined };

function useRatingQueryID(): [string, string, string] {
	const { userInfo } = useAuth();
	return [...RATING_PROMPT_STATE_QUERY_KEY, userInfo.id];
}

function useRatingPromptState(): UseQueryResult<RatingPromptState> {
	const queryKey = useRatingQueryID();
	return useQuery({
		queryKey,
		queryFn: backendService.getRatingPromptState,
	});
}

function useMarkRatingPromptShown(): UseMutationResult<void, unknown, void> {
	const queryKey = useRatingQueryID();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: backendService.markRatingPromptShown,
		onMutate: () => {
			queryClient.setQueryData<RatingPromptState>(queryKey, (prev) => ({
				lastReviewPromptAt: DateTime.utc(),
				approvedShiftRequestsCount: prev?.approvedShiftRequestsCount ?? 0,
				mostRecentApprovalAt: prev?.mostRecentApprovalAt,
			}));
		},
	});
}

export function useShowReviewPopup(): () => Promise<void> {
	const { userInfo } = useAuth();
	const { track, trackError } = useAnalytics();
	const isFlagEnabled = useFeatureFlag(FEATURE_FLAGS.InAppRatingPrompt);

	const { data: ratingState, isLoading } = useRatingPromptState();
	const { mutateAsync: markShown } = useMarkRatingPromptShown();

	const accountAgeInDays = userInfo.created_at ? DateTime.now().diff(userInfo.created_at, "days").days : 0;

	const isEligible =
		Capacitor.isNativePlatform() &&
		isFlagEnabled &&
		!isLoading &&
		accountAgeInDays >= MIN_ACCOUNT_AGE_DAYS &&
		!!ratingState &&
		!ratingState.lastReviewPromptAt &&
		ratingState.approvedShiftRequestsCount >= APPROVED_SHIFTS_TRIGGER_COUNT &&
		!!ratingState.mostRecentApprovalAt &&
		DateTime.now().diff(ratingState.mostRecentApprovalAt, "days").days <= MAX_APPROVAL_AGE_DAYS;

	const showReviewPopup = useCallback(async (): Promise<void> => {
		track(RATING_PROMPT_ANALYTICS_EVENT, { action: "trigger" });
		if (!isEligible) {
			track(RATING_PROMPT_ANALYTICS_EVENT, { action: "skip", isEligible });
			return;
		}

		try {
			const { InAppReview } = await import("@capacitor-community/in-app-review");
			await InAppReview.requestReview();

			track(RATING_PROMPT_ANALYTICS_EVENT, { action: "show" });
		} catch (e) {
			console.error(e);

			const message = e instanceof Error ? e.message : String(e);
			trackError(RATING_PROMPT_ANALYTICS_EVENT, { action: "show", message });
		}

		try {
			await markShown();
			track(RATING_PROMPT_ANALYTICS_EVENT, { action: "mark_shown" });
		} catch (e) {
			console.error(e);

			const message = e instanceof Error ? e.message : String(e);
			trackError(RATING_PROMPT_ANALYTICS_EVENT, { action: "mark_shown", message });
		}
	}, [track, isEligible, trackError, markShown]);

	return showReviewPopup;
}
